const ISSUER_AWARE_ENDPOINTS = new Set([
	"cards/list",
	"cardholders/form",
	"cardholders/submit",
	"companies/transactions",
	"companies/transactions/cards",
	"companies/transactions/all",
	"companies/transactions/card",
	"companies/transactions/buyer",
	"companies/stats/spend-overview",
	"companies/stats/cards-spend-overview",
	"companies/stats/overview",
	"companies/stats/by-card",
	"companies/stats/teams-month",
	"companies/stats/top-teams",
	"companies/stats/top-buyers",
	"companies/stats/declined-by-team",
	"companies/stats/declined-by-buyer",
	"teams/list",
	"teams/users",
	"teams/user/topup",
	"teams/user/withdraw",
	"teams/transactions",
	"teams/stats/money-inflow",
	"teams/stats/top-spenders",
	"teams/stats/transaction-status",
	"users/list",
	"users/get",
	"users/get/me",
]);

let activeCompanyUid: string | null = null;
let activeIssuer: number | null = null;

export const setIssuerRequestContext = (companyUid: string | null, issuer: number | null) => {
	activeCompanyUid = companyUid;
	activeIssuer = Number.isInteger(issuer) ? issuer : null;
};

export const getIssuerRequestContext = () => ({
	companyUid: activeCompanyUid,
	issuer: activeIssuer,
});

export type IssuerRequestContextSnapshot = ReturnType<typeof getIssuerRequestContext>;

export const isIssuerRequestContextCurrent = (snapshot: IssuerRequestContextSnapshot) =>
	snapshot.companyUid === activeCompanyUid && snapshot.issuer === activeIssuer;

const getEndpoint = (method: string) => method.split("?", 1)[0].replace(/^\/+/, "");

const getQuery = (method: string) => {
	const queryStart = method.indexOf("?");
	return queryStart >= 0 ? method.slice(queryStart + 1) : "";
};

const hasIssuerInQuery = (method: string) => {
	const query = getQuery(method);
	return query ? new URLSearchParams(query).has("issuer") : false;
};

const hasExplicitIssuer = (req: IAPIRequest) => {
	if (hasIssuerInQuery(req.method)) return true;
	if (req.params && req.params.issuer !== undefined) return true;
	return !!(
		req.data &&
		typeof req.data === "object" &&
		!(req.data instanceof FormData) &&
		"issuer" in req.data &&
		(req.data as Record<string, unknown>).issuer !== undefined
	);
};

const isCardTemplateRequest = (req: IAPIRequest) => {
	if (getEndpoint(req.method) !== "cards/get") return false;
	if (
		req.data &&
		typeof req.data === "object" &&
		!(req.data instanceof FormData) &&
		String((req.data as Record<string, unknown>).uid ?? "") === "-1"
	) {
		return true;
	}
	const query = getQuery(req.method);
	return query ? new URLSearchParams(query).get("uid") === "-1" : false;
};

export const isIssuerAwareRequest = (req: IAPIRequest) => {
	const endpoint = getEndpoint(req.method);
	if (endpoint === "cards/get") return isCardTemplateRequest(req);
	return ISSUER_AWARE_ENDPOINTS.has(endpoint);
};

/**
 * Adds the active issuer only to endpoints that explicitly accept it.
 * Card creation/top-up/withdrawal and account/partner requests are deliberately
 * absent: their issuer is derived by the backend from the BIN, card or channel.
 */
export const withActiveIssuer = (req: IAPIRequest): IAPIRequest => {
	if (activeIssuer === null || !isIssuerAwareRequest(req) || hasExplicitIssuer(req)) return req;

	const httpMethod = (req.httpMethod ?? "POST").toUpperCase();
	if (httpMethod === "GET") {
		return {
			...req,
			params: { ...(req.params ?? {}), issuer: activeIssuer },
		};
	}

	const data =
		req.data && typeof req.data === "object" && !(req.data instanceof FormData)
			? req.data
			: {};
	return {
		...req,
		data: { ...(data as Record<string, unknown>), issuer: activeIssuer },
	};
};
